import { format } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';
import Icon from '@mdi/react';
import { mdiCashRefund, mdiCheck } from '@mdi/js';
import { CurrencyType } from '../../../api/server/models/CurrencyType';
import { ProviderPatientStatement } from '../../../api/server/models/ProviderPatientStatement';
import { IToastContextProps } from '../../../services/toast/toast';
import { BaseInput, DatePicker, Variables } from '../../base';
import { IClaimStore, IDialogContext } from '../statement-details.types';
import { formatLedgerMoney } from './statement-apply-to-claims.utils';
import { parseLedgerAmount, parseMoney } from '../utils/statement-format.utils';
import * as S from './statement-ledger-dialog.styles';

export type RefundablePaymentOption = {
    paymentId: string;
    paymentSourceId: string;
    paymentDate?: string;
    amount: number;
    externalTransactionId?: string;
    claimLabels: string[];
};

/** Payment sources that still have a positive allocated patient payment and no refund yet. */
export const getRefundablePaymentOptions = (
    statement: ProviderPatientStatement | undefined,
): RefundablePaymentOption[] => {
    if (!statement) return [];

    const options: RefundablePaymentOption[] = [];

    for (const source of statement.paymentSources || []) {
        if (!source.id) continue;

        const refundablePaymentIds: string[] = [];
        const claimLabels: string[] = [];

        for (const claim of statement.claims || []) {
            const payments = claim.patientPayments || [];
            const isRefunded = payments.some(
                patientPayment =>
                    patientPayment.collectionPaymentSourceId === source.id &&
                    parseMoney(patientPayment.amount?.amount) < 0,
            );

            if (isRefunded) continue;

            for (const patientPayment of payments) {
                if (
                    patientPayment.collectionPaymentSourceId === source.id &&
                    patientPayment.id &&
                    parseMoney(patientPayment.amount?.amount) > 0
                ) {
                    refundablePaymentIds.push(patientPayment.id);
                    const label = claim.externalId || claim.id;
                    if (label && !claimLabels.includes(label)) {
                        claimLabels.push(label);
                    }
                }
            }
        }

        if (!refundablePaymentIds.length) continue;

        options.push({
            paymentId: refundablePaymentIds[0],
            paymentSourceId: source.id,
            paymentDate: source.paymentDate,
            amount: parseMoney(source.totalAmount?.amount) || parseMoney(source.allocatedAmount?.amount),
            externalTransactionId: source.externalTransactionId,
            claimLabels,
        });
    }

    return options;
};

type RecordRefundDialogProps = {
    patientStatement?: ProviderPatientStatement;
    onClose: () => void;
    onSuccess: () => void;
    claimStore: IClaimStore;
    dialogContext: IDialogContext;
    toastContext: IToastContextProps;
};

export const RecordRefundDialog: React.FunctionComponent<RecordRefundDialogProps> = props => {
    const { claimStore, dialogContext, toastContext, patientStatement, onClose, onSuccess } = props;

    const refundablePayments = useMemo(
        () => getRefundablePaymentOptions(patientStatement),
        [patientStatement],
    );

    const [selectedPaymentId, setSelectedPaymentId] = useState<string>('');
    const [refundDate, setRefundDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
    const [amount, setAmount] = useState<string>('');
    const [memo, setMemo] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        if (!selectedPaymentId && refundablePayments.length === 1) {
            const only = refundablePayments[0];
            setSelectedPaymentId(only.paymentId);
            setAmount(only.amount > 0 ? only.amount.toFixed(2) : '');
        }
    }, [refundablePayments, selectedPaymentId]);

    const selectedPayment = refundablePayments.find(item => item.paymentId === selectedPaymentId);

    const selectPayment = (option: RefundablePaymentOption): void => {
        setSelectedPaymentId(option.paymentId);
        setAmount(option.amount > 0 ? option.amount.toFixed(2) : '');
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};
        const parsedAmount = parseLedgerAmount(amount);

        if (!selectedPaymentId) {
            newErrors.payment = 'Select a payment to refund';
        }

        if (!refundDate) {
            newErrors.refundDate = 'Refund date is required';
        }

        if (!amount || parsedAmount <= 0) {
            newErrors.amount = 'Refund amount is required and must be greater than 0';
        }

        if (selectedPayment && parsedAmount > selectedPayment.amount + 0.005) {
            newErrors.amount = 'Refund amount cannot exceed the original payment';
        }

        if (!memo || memo.trim() === '') {
            newErrors.memo = 'Refund memo is required';
        }

        if (!claimStore.refundCollectionPayment) {
            newErrors.payment = 'Refund is not supported in this app';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (): Promise<void> => {
        if (!validate() || !claimStore.refundCollectionPayment) {
            return;
        }

        setIsSubmitting(true);

        try {
            await claimStore.refundCollectionPayment(selectedPaymentId, {
                refundDate,
                refundAmount: {
                    amount: parseLedgerAmount(amount).toFixed(2),
                    currency: CurrencyType.USD,
                },
                memo: memo.trim(),
            });

            toastContext.notify?.('Refund was processed successfully.', 'right');
            onSuccess();
        } catch (err) {
            dialogContext.showError(err as Error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDateChange = (date: Date | undefined): void => {
        if (date) {
            setRefundDate(format(date, 'yyyy-MM-dd'));
        } else {
            setRefundDate('');
        }
    };

    return (
        <S.DialogBody>
            <S.Header>
                <S.HeaderIcon path={mdiCashRefund} size={0.9} />
                <S.HeaderTitle>Record refund</S.HeaderTitle>
            </S.Header>

            <S.FormSection>
                <S.Label>Payment to refund</S.Label>
                <S.ClaimList>
                    {refundablePayments.map(option => {
                        const checked = option.paymentId === selectedPaymentId;
                        return (
                            <S.ClaimRow
                                key={option.paymentSourceId}
                                type="button"
                                variant="unstyled"
                                onClick={() => selectPayment(option)}>
                                <S.CheckBox checked={checked}>
                                    {checked && (
                                        <Icon path={mdiCheck} size={0.55} color={Variables.Swatches.White} />
                                    )}
                                </S.CheckBox>
                                <S.ClaimMeta>
                                    <S.ClaimTitle>
                                        {option.externalTransactionId
                                            ? `Payment ${option.externalTransactionId}`
                                            : `Payment ${option.paymentSourceId.slice(0, 8)}`}
                                    </S.ClaimTitle>
                                    <S.ClaimSubtitle>
                                        {option.paymentDate || '—'}
                                        {option.claimLabels.length
                                            ? ` · Claim #${option.claimLabels.join(', #')}`
                                            : ''}
                                    </S.ClaimSubtitle>
                                </S.ClaimMeta>
                                <S.ClaimBalance>{formatLedgerMoney(option.amount)}</S.ClaimBalance>
                            </S.ClaimRow>
                        );
                    })}
                    {!refundablePayments.length && (
                        <S.ClaimRow type="button" variant="unstyled" disabled>
                            <S.ClaimMeta>
                                <S.ClaimTitle>No refundable payments</S.ClaimTitle>
                                <S.ClaimSubtitle>
                                    Only payments that have not already been refunded can be selected.
                                </S.ClaimSubtitle>
                            </S.ClaimMeta>
                        </S.ClaimRow>
                    )}
                </S.ClaimList>
                {errors.payment && <S.ErrorText>{errors.payment}</S.ErrorText>}
            </S.FormSection>

            <S.FormSection>
                <S.Label>Refund date</S.Label>
                <DatePicker
                    value={refundDate}
                    onDateSelected={handleDateChange}
                    errorMessage={errors.refundDate}
                />
            </S.FormSection>

            <S.FormSection>
                <S.Label>Refund amount</S.Label>
                <S.AmountField>
                    <S.AmountPrefix>$</S.AmountPrefix>
                    <BaseInput
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={amount}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAmount(e.target.value)}
                        disableLatin1Validation
                    />
                </S.AmountField>
                {errors.amount && <S.ErrorText>{errors.amount}</S.ErrorText>}
            </S.FormSection>

            <S.FormSection>
                <S.Label>Refund memo</S.Label>
                <BaseInput
                    value={memo}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMemo(e.target.value)}
                    errorMessage={errors.memo}
                    placeholder="Enter refund memo"
                    required
                />
            </S.FormSection>

            <S.ButtonGroup
                changed={true}
                useNotify={false}
                onCancel={onClose}
                onSave={handleSubmit}
                saveLabel={
                    isSubmitting
                        ? 'Processing...'
                        : `Refund $${parseLedgerAmount(amount).toFixed(2)}`
                }
                saveNotice="Refund processed successfully"
            />
        </S.DialogBody>
    );
};
